import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { formatNumber } from '@/i18n/format';
import { DemoBadgeVariant } from '@/components/demo-badge/demo-badge.types';
import { KpiDelta, KpiTileVariant } from '@/components/kpi-tile/kpi-tile.types';
import {
  CallMetricChartPoint,
  CallMetrics,
  InsightOverallSatisfaction,
  Metric,
} from '@/services/dashboard-service/dashboard.service.types';
import { TrendPoint } from '@/components/trend-chart/trend-chart.utils';
import { BusinessHourItem, BusinessHours } from '@/services/business-hours/business-hours.types';
import { averageRating, formatScoreOutOfFive } from '@/utils/score';
import { demoAppointments, showDemoData } from '@/demo/demo-data';
import { calculateSecondsToMinutes, formatTimeRange, getHoursForDate } from '@/utils/helpers';
import { MINUTES_SAVED_PER_CASE, rangeKey } from './dashboard.constants';

export type KpiTileModel = {
  key: string;
  label: string;
  value: string;
  unit?: string;
  note?: string;
  delta?: KpiDelta;
  variant?: KpiTileVariant;
  demoBadge?: DemoBadgeVariant;
};

type BuildKpiTilesArgs = {
  metrics?: CallMetrics;
  satisfaction?: InsightOverallSatisfaction;
  preset: string | null;
};

// Delta and tile figures are always shown to one decimal, so a flat move reads "0,0" and the
// threshold in `toDelta` (under 0.05 prints as 0.0) stays true to what is on screen.
const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 } as const;

/** Rounds first: several metric leaves are floats, so a count of three arrives as 3.0. */
export const asCount = (value: number) => formatNumber(Math.round(value));

/** Absent is not zero: a leaf the payload omits renders as a dash. */
export const asOptionalCount = (value?: number) => (value === undefined ? '—' : asCount(value));

/** Absent and zero both read as no data: a real mean is never 0. */
export const measuredDuration = (metric?: Metric | null) =>
  metric && Number.isFinite(metric.value) && metric.value > 0 ? metric : undefined;

/**
 * Null change means no baseline period, which is not a change of zero.
 * Three units, not interchangeable: a count's change is a relative percent, a
 * share's is a percentage-point move, and a rating moves on its own scale and takes
 * no suffix. The mockup suffixes the second ` p.p.`.
 */
export function toDelta(
  change: number | null,
  unit: '%' | ' p.p.' | '' = '%'
): KpiDelta | undefined {
  if (change === null || !Number.isFinite(change)) return undefined;

  const magnitude = Math.abs(change);
  // Threshold, not equality: the figure is shown to one decimal, so anything under
  // 0.05 prints as 0.0 and an arrow beside it would contradict itself.
  const direction = magnitude < 0.05 ? 'flat' : change > 0 ? 'up' : 'down';

  return { direction, value: `${formatNumber(magnitude, ONE_DECIMAL)}${unit}` };
}

/**
 * The seven measured tiles, plus the fabricated eighth when the demo flag is on. Labels
 * are written from what each metric actually counts rather than from its payload key:
 * `inbound_calls` has no direction behind it, and the two rate metrics are shares of
 * answered calls.
 */
export function buildKpiTiles({
  metrics,
  satisfaction,
  preset,
}: BuildKpiTilesArgs): KpiTileModel[] {
  const resolved = metrics?.ai_resolution;
  const rating = averageRating(satisfaction);
  const duration = measuredDuration(metrics?.average_call_duration);
  const savedHours = ((resolved?.value ?? 0) * MINUTES_SAVED_PER_CASE) / 60;

  return [
    {
      key: 'calls',
      label: t('dashboard:kpis.calls.label', {
        range: t(`dashboard:kpis.calls.range.${rangeKey(preset)}`),
      }),
      value: asCount(metrics?.inbound_calls.value ?? 0),
      note: t('dashboard:kpis.calls.note'),
      delta: toDelta(metrics?.inbound_calls.change ?? null),
      variant: 'hero',
    },
    {
      key: 'resolved',
      label: t('dashboard:kpis.resolved.label'),
      value: asCount(resolved?.value ?? 0),
      unit: `· ${Math.round(resolved?.current_percent ?? 0)}%`,
      note: t('dashboard:kpis.resolved.note'),
      delta: toDelta(resolved?.change ?? null, ' p.p.'),
    },
    {
      key: 'handed-off',
      label: t('dashboard:kpis.handedOff.label'),
      value: asCount(metrics?.agent_handoff.value ?? 0),
      note: t('dashboard:kpis.handedOff.note'),
      delta: toDelta(metrics?.agent_handoff.change ?? null, ' p.p.'),
    },
    {
      key: 'time-saved',
      label: t('dashboard:kpis.timeSaved.label'),
      value: `≈ ${formatNumber(savedHours, ONE_DECIMAL)}`,
      unit: t('metrics:units.hoursShort'),
      note: t('dashboard:kpis.timeSaved.note', { minutes: MINUTES_SAVED_PER_CASE }),
    },
    {
      key: 'after-hours',
      label: t('dashboard:kpis.afterHours.label'),
      value: asOptionalCount(metrics?.out_of_hours_calls?.value),
      note: t('dashboard:kpis.afterHours.note'),
      delta: metrics?.out_of_hours_calls ? toDelta(metrics.out_of_hours_calls.change) : undefined,
    },
    {
      key: 'average-duration',
      label: t('dashboard:kpis.averageDuration.label'),
      value: duration ? calculateSecondsToMinutes(duration.value) : '—',
      unit: t('metrics:units.minutesShort'),
      note: t('dashboard:kpis.averageDuration.note'),
      // One gate for both: a dashed value beside a measured delta reads as no data and a
      // total collapse at once.
      delta: duration ? toDelta(duration.change) : undefined,
    },
    {
      key: 'rating',
      label: t('dashboard:kpis.rating.label'),
      value: formatScoreOutOfFive(rating),
      unit: '/ 5',
      note: t('dashboard:kpis.rating.note', { count: satisfaction?.responses ?? 0 }),
    },
    // Fabricated, so it is absent rather than badged when the flag is off.
    ...(showDemoData
      ? [
          {
            key: 'appointments',
            label: t('dashboard:kpis.appointments.label'),
            value: asCount(demoAppointments),
            note: t('dashboard:kpis.appointments.note'),
            demoBadge: 'not-ported' as const,
          },
        ]
      : []),
  ];
}

/**
 * Assistant-handled calls: answered minus handed off. `agent` is a subset of `ai`,
 * so the clamp only catches a malformed bucket. All-agent tenants arrive as
 * `ai: 0`, and zero is truthful there — the assistant handled nothing.
 */
export function toTrendSeries(chart: CallMetricChartPoint[]): TrendPoint[] {
  return chart.map((point) => ({
    date: point.date,
    value: Math.max(0, point.ai - point.agent),
  }));
}

/**
 * Whether the buckets are hourly. Day buckets arrive as `YYYY-MM-DD`, hour buckets
 * as ISO-8601 with a time part, so the shape of the key is the granularity.
 */
export function trendPointsAreHourly(chart: CallMetricChartPoint[]): boolean {
  return chart.some((point) => point.date.includes('T'));
}

/**
 * The out-of-hours share, from the payload's counts not the shaded bars, so it cannot
 * disagree with the tile above. Null with no leaf, and with no calls to be a share of.
 */
export function afterHoursShare(metrics?: CallMetrics): number | null {
  const outOfHours = metrics?.out_of_hours_calls;
  const total = metrics?.inbound_calls?.value;

  if (!outOfHours || !Number.isFinite(outOfHours.value)) return null;
  if (!total || !Number.isFinite(total)) return null;

  return Math.round((outOfHours.value / total) * 100);
}

export const HOURS_IN_DAY = 24;

/** The 24-slot series, or null: the chart bands by index, so a short one bands wrong bars. */
export function hourOfDaySeries(metrics?: CallMetrics): number[] | null {
  const series = metrics?.hourly_calls;

  if (!series || series.length !== HOURS_IN_DAY) return null;
  if (!series.every((calls) => Number.isFinite(calls) && calls >= 0)) return null;

  return series;
}

/** Any calls at all. The series is dense, so 24 zeros is a quiet window, not a gap. */
export function hasHourlyVolume(series: number[] | null): boolean {
  return Boolean(series?.some((calls) => calls > 0));
}

export type OfficeWindow = {
  /** Undefined when no day in scope resolves, so nothing is shaded on a guess. */
  openHour?: number;
  closeHour?: number;
  hint: string;
};

/** True only when some hour falls inside the window, so both bar tones appear. */
export function hasOpenHours(office: OfficeWindow): boolean {
  return (
    office.openHour !== undefined &&
    office.closeHour !== undefined &&
    office.closeHour > office.openHour
  );
}

/** Minutes since midnight, read from the string as written rather than converted. */
function clockMinutes(iso: string): number | null {
  const clock = /T(\d{2}):(\d{2})/.exec(iso);

  return clock ? Number(clock[1]) * 60 + Number(clock[2]) : null;
}

/**
 * Closed means the flag says so, or the window has zero width. An all-day row carries
 * that same zero-width clock and means the opposite, so it is excluded by name.
 */
function isClosedRow(row: BusinessHourItem): boolean {
  if (row.is_closed) return true;
  if (row.all_day) return false;
  if (!row.start_time || !row.end_time) return false;

  return clockMinutes(row.start_time) === clockMinutes(row.end_time);
}

/**
 * One day's window, today unless a caller names another.
 *
 * `getHoursForDate` matches temporary rows on their own date — the payload carries
 * a whole month, so a day outside it falls back to its permanent weekday row. A split
 * day spans earliest open to latest close, and a known closure returns a zero-width
 * window, which an unknown one cannot.
 */
export function officeWindow(hours?: BusinessHours, day: Dayjs = dayjs()): OfficeWindow {
  const rows = getHoursForDate(hours?.temporary ?? [], hours?.permanent ?? [], day);

  if (!rows.length) return { hint: t('dashboard:office.unavailable') };

  // An all-day row has no meaningful clock to span, and the 00:00-to-00:00 window
  // it usually carries would otherwise shade the whole day shut.
  if (rows.some((row) => row.all_day && !row.is_closed)) {
    return { openHour: 0, closeHour: 24, hint: t('dashboard:office.allDay') };
  }

  if (rows.every(isClosedRow)) {
    return { openHour: 0, closeHour: 0, hint: t('dashboard:office.closedToday') };
  }

  const open = rows.filter((row) => !isClosedRow(row) && row.start_time && row.end_time);
  const starts = open.map((row) => clockMinutes(row.start_time!)).filter((m) => m !== null);
  const ends = open.map((row) => clockMinutes(row.end_time!)).filter((m) => m !== null);

  if (!starts.length || !ends.length) return { hint: t('dashboard:office.unavailable') };

  const earliest = Math.min(...starts);
  const latest = Math.max(...ends);

  // A window that ends at or before it starts spans midnight, which every consumer
  // would read inside out: all bars off, the callout claiming everything.
  if (latest <= earliest) return { hint: t('dashboard:office.unavailable') };
  const spanning = open.find((row) => clockMinutes(row.start_time!) === earliest)!;
  const closing = open.find((row) => clockMinutes(row.end_time!) === latest)!;

  return {
    // Floor the open edge and ceil the close edge, so a bar covering any open
    // minute reads open. Truncating both would shade a 15:30 close shut at 15:00.
    openHour: Math.floor(earliest / 60),
    closeHour: Math.ceil(latest / 60),
    hint: t('dashboard:office.hours', {
      hours: formatTimeRange(spanning.start_time, closing.end_time) ?? '',
    }),
  };
}

/** A window with hours to shade by, known or a known closure. */
const isResolved = (window: OfficeWindow) =>
  window.openHour !== undefined && window.closeHour !== undefined;

const clockHour = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/**
 * One window for every day a range covers, because the bars count the whole range and a
 * single day's clock cannot speak for it. Closed days drop out of the union instead of
 * shading the range shut — the reason a holiday used to mark a week of weekday calls
 * after-hours. A range closed throughout keeps the zero-width window.
 *
 * The union errs open: an hour any day in the range was open for is not shaded off.
 */
export function officeWindowForRange(
  hours: BusinessHours | undefined,
  days: Dayjs[]
): OfficeWindow {
  if (days.length <= 1) return officeWindow(hours, days[0]);

  const resolved = days.map((day) => officeWindow(hours, day)).filter(isResolved);

  if (!resolved.length) return { hint: t('dashboard:office.unavailable') };

  const open = resolved.filter(hasOpenHours);

  if (!open.length) {
    return { openHour: 0, closeHour: 0, hint: t('dashboard:office.closedEveryDay') };
  }

  const openHour = Math.min(...open.map((window) => window.openHour!));
  const closeHour = Math.max(...open.map((window) => window.closeHour!));
  const hints = new Set(open.map((window) => window.hint));

  return {
    openHour,
    closeHour,
    // Days that agree keep their own copy, minutes and all. Only a range whose days
    // differ falls back to the hour-precision span the shading actually uses.
    hint:
      hints.size === 1
        ? [...hints][0]
        : t('dashboard:office.widest', { open: clockHour(openHour), close: clockHour(closeHour) }),
  };
}
